import { useState } from 'react';
import type { AssistantExportable, AssistantPanelProps, ChatTurn } from './types';
import { isAssistantError } from './types';
import { MessageBubble } from './MessageBubble';
import { ExportSheetButton } from './ExportSheetButton';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  toolsUsed?: string[];
  data?: unknown;
  isError?: boolean;
  exportable?: AssistantExportable | null;
}

/**
 * App-agnostic chat drawer. Holds the multi-turn thread, sends each question to
 * the injected transport with the prior turns as context, and renders prose +
 * generic source rows. No endpoint/auth/domain knowledge lives here.
 */
export function AssistantPanel({
  open,
  onClose,
  transport,
  exportToSheet,
  examplePrompts = [],
  title = 'Assistant',
  className = '',
}: AssistantPanelProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  async function send(text: string) {
    const message = text.trim();
    if (!message || loading) return;
    // History BEFORE this message becomes the multi-turn context (errors excluded).
    const recentTurns: ChatTurn[] = messages
      .filter((m) => !m.isError)
      // The query contract accepts 20 turns of up to 4,000 characters each.
      // Bound transmitted context without truncating the visible conversation.
      .slice(-20)
      .map((m) => ({ role: m.role, content: m.content.slice(0, 4000) }));

    setMessages((prev) => [...prev, { role: 'user', content: message }]);
    setInput('');
    setLoading(true);
    try {
      const ans = await transport({ message, recentTurns });
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: ans.answer,
          toolsUsed: ans.toolsUsed,
          data: ans.data,
          exportable: ans.exportable ?? null,
        },
      ]);
    } catch (e) {
      const content = isAssistantError(e) ? e.message : 'Something went wrong — please try again.';
      setMessages((prev) => [...prev, { role: 'assistant', content, isError: true }]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className={`ra-panel fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-white shadow-2xl ${className}`}
      role="dialog"
      aria-label={title}
    >
      <header className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-neutral-900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close assistant"
          className="text-neutral-400 hover:text-neutral-700"
        >
          ✕
        </button>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {messages.length === 0 && (
          <div className="space-y-2">
            <p className="text-sm text-neutral-500">Ask a question to get started.</p>
            {examplePrompts.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => send(p)}
                className="block w-full rounded-md border border-neutral-200 px-3 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
              >
                {p}
              </button>
            ))}
          </div>
        )}

        {messages.map((m, i) =>
          m.isError ? (
            <div
              key={i}
              role="alert"
              className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {m.content}
            </div>
          ) : (
            <div key={i}>
              <MessageBubble
                turn={{ role: m.role, content: m.content }}
                toolsUsed={m.toolsUsed}
                data={m.data}
              />
              {m.role === 'assistant' && m.exportable && exportToSheet && (
                <ExportSheetButton exportable={m.exportable} exportToSheet={exportToSheet} />
              )}
            </div>
          ),
        )}

        {loading && (
          <div role="status" className="text-sm text-neutral-400">
            Thinking…
          </div>
        )}
      </div>

      <form
        className="border-t border-neutral-200 p-3"
        onSubmit={(e) => {
          e.preventDefault();
          void send(input);
        }}
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send(input);
            }
          }}
          rows={2}
          placeholder="Ask about scheduling…"
          className="w-full resize-none rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </form>
    </div>
  );
}
